/**
 * TeacherMyClasses.jsx
 * ────────────────────
 * Teacher's "My Classes" view — rectangle cards grouped by Department → Batch.
 * Click a class → opens a big, full-screen Deskey-themed modal with:
 *   • Chapters (lessons) sidebar + create-chapter
 *   • Topics timeline per chapter (daily publishes with preview)
 *   • Info / Students / Pending / Add Student tabs
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Users, Mail, CheckCircle2, Search, Plus, Trash2, ChevronRight, GraduationCap,
  BookOpen, FileText, Calendar, Sparkles, Layers, Clock, Eye, Pencil, Activity,
  ClipboardList, CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';
import { confirmDialog } from '@/components/ConfirmDialog';
import ClassInsightsPanel from '@/components/teacher/ClassInsightsPanel';
import SessionPreview from '@/components/teacher/ContentEditor/SessionPreview';
import AssignmentBlock from '@/components/AssignmentBlock';
import { notifyError } from '../lib/errors';
import Loader from './Loader';
import { PRIMARY_BTN, SECONDARY_BTN, CARD, CARD_HOVER, StatusDot, PageHeading, EmptyState, ClassTile, AccentRule } from '@/components/teacher/teacherUi';
import { classColor } from '@/components/teacher/classIdentity';

const API = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const token = localStorage.getItem('desky_token') || '';
  const res = await fetch(`${API}/api${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  const txt = await res.text();
  try {
    return JSON.parse(txt);
  } catch {
    return { detail: txt };
  }
};

// ── Class Card ──────────────────────────────────────────────────────────────
function ClassCard({ cls, color, onOpen, onQuickPick }) {
  const subjectLine = (cls.subjects || []).join(' · ') || 'Subject';
  const capacity = cls.capacity || 0;
  const open = () => onOpen(cls, color);
  return (
    <div
      className={`group ${CARD} ${CARD_HOVER} cursor-pointer p-5`}
      data-testid={`class-card-${cls.id}`}
      onClick={open}
    >
      <div className="flex items-start gap-3">
        <ClassTile size="lg" icon={Layers} color={color} />
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-semibold text-slate-900 truncate">{cls.name}</h4>
          <p className="text-sm text-slate-600 truncate capitalize">{subjectLine}</p>
        </div>
        {cls.class_code && <p className="text-xs font-mono text-slate-400 flex-shrink-0 mt-1">{cls.class_code}</p>}
      </div>

      <div className="mt-5 flex items-baseline justify-between text-sm">
        <span className="text-slate-600 flex items-center gap-1.5"><Users className="w-4 h-4 text-slate-400" />Students</span>
        <span className="tabular-nums text-slate-900">{cls.student_count}<span className="text-slate-400"> / {capacity || '∞'}</span></span>
      </div>

      {(cls.lesson_count > 0 || cls.pending_count > 0) && (
        <div className="mt-4 flex items-center gap-4 text-sm text-slate-600 tabular-nums">
          {cls.lesson_count > 0 && (
            <span className="flex items-center gap-1.5"><BookOpen className="w-4 h-4 text-slate-400" />{cls.lesson_count} {cls.lesson_count === 1 ? 'lesson' : 'lessons'}</span>
          )}
          {cls.pending_count > 0 && <StatusDot tone="working" label={`${cls.pending_count} waiting to join`} className="text-sm text-slate-700" />}
        </div>
      )}

      <div className="mt-5 flex items-center gap-2">
        {onQuickPick && (
          <button
            onClick={(e) => { e.stopPropagation(); onQuickPick(cls); }}
            className={`${PRIMARY_BTN} flex-1`}
            data-testid={`class-pick-${cls.id}`}
          >
            Select
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); open(); }}
          className={`${SECONDARY_BTN} ${onQuickPick ? '' : 'flex-1'} flex items-center gap-1 justify-center`}
          data-testid={`class-details-${cls.id}`}
        >
          Open class <ChevronRight className="w-4 h-4 text-slate-400 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
}

// ── Grouped Class Grid ──────────────────────────────────────────────────────
export function GroupedClassGrid({ groups, onOpenClass, onQuickPick, emptyMessage }) {
  if (!groups || groups.length === 0) {
    return <EmptyState icon={GraduationCap} title={emptyMessage || 'No classes assigned yet'} />;
  }
  return (
    <div className="space-y-10" data-testid="grouped-class-grid">
      {groups.map((g, gi) => {
        const color = classColor(g.department.sticker_color, g.department.id);
        return (
          <section key={g.department.id + gi} data-testid={`dept-group-${g.department.id}`}>
            <div className="mb-5">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} aria-hidden="true" />
                <h3 className="text-xl font-semibold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{g.department.name}</h3>
              </div>
              <AccentRule className="mt-2" color={color} />
            </div>
            <div className="space-y-6">
              {g.batches.map((b, bi) => (
                <div key={b.batch.id + bi} data-testid={`batch-group-${b.batch.id}`}>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-sm font-medium text-slate-700">{b.batch.name}</span>
                    {b.batch.year && b.batch.year !== b.batch.name.replace(/^Batch\s*/i, '') && <span className="text-sm text-slate-500 tabular-nums">{b.batch.year}</span>}
                    <span className="text-sm text-slate-500 ml-auto tabular-nums">{b.classes.length} {b.classes.length === 1 ? 'class' : 'classes'}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {b.classes.map((c) => (
                      <ClassCard key={c.id} cls={c} color={color} onOpen={onOpenClass} onQuickPick={onQuickPick} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
//  Class Detail Modal — BIG full-screen Deskey-themed popup
//  Exposes the same public API: <ClassDetailDrawer classId onClose onStudentsChanged />
// ══════════════════════════════════════════════════════════════════════════
export function ClassDetailDrawer({ classId, color, onClose, onStudentsChanged }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [students, setStudents] = useState([]);
  const [pending, setPending] = useState([]);
  const [available, setAvailable] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('lessons'); // lessons | info | students | pending | assignments | add
  const [busy, setBusy] = useState(false);

  // New-assignment form (Assignments tab)
  const [showNewAssignment, setShowNewAssignment] = useState(false);
  const [newAssignmentChapterId, setNewAssignmentChapterId] = useState('');
  const [newAssignmentTopicId, setNewAssignmentTopicId] = useState('');
  const [newAssignment, setNewAssignment] = useState({
    title: '', description: '', dueDate: '', dueTime: '23:59', points: 0,
  });

  // Chapter/Topic navigation state
  const [selectedChapterId, setSelectedChapterId] = useState(null);
  const [topicsByChapter, setTopicsByChapter] = useState({}); // { [chapter_id]: topics[] }
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [viewingTopic, setViewingTopic] = useState(null);
  const [chapterSearch, setChapterSearch] = useState('');
  const [topicSearch, setTopicSearch] = useState('');

  // Chapter edit inline form
  const [editingChapterId, setEditingChapterId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');

  // New-chapter form
  const [showNewChapter, setShowNewChapter] = useState(false);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [newChapterDesc, setNewChapterDesc] = useState('');

  const reload = async () => {
    try {
      const [d, s, p, a, l, asg] = await Promise.all([
        api(`/teacher/classes/${classId}/detail`),
        api(`/teacher/classes/${classId}/students`),
        api(`/teacher/classes/${classId}/pending-enrollments`),
        api(`/teacher/classes/${classId}/available-students`),
        api(`/teacher/classes/${classId}/lessons`),
        api(`/teacher/assignments?class_id=${classId}`),
      ]);
      if (d?.id) setDetail(d);
      if (s?.students) setStudents(s.students);
      if (p?.enrollments) setPending(p.enrollments);
      if (a?.students) setAvailable(a.students);
      if (l?.lessons) {
        setChapters(l.lessons);
        setSelectedChapterId((prev) => prev || (l.lessons[0]?.id ?? null));
      }
      if (asg?.assignments) setAssignments(asg.assignments);
    } catch { /* silent */ }
    setInitialLoading(false);
  };

  const loadTopics = async (lessonId, { force = false } = {}) => {
    if (!lessonId) return;
    if (!force && topicsByChapter[lessonId]) return;
    setLoadingTopics(true);
    try {
      const r = await api(`/teacher/lessons/${lessonId}/topics`);
      if (r.topics) setTopicsByChapter(prev => ({ ...prev, [lessonId]: r.topics }));
    } catch { /* silent */ }
    setLoadingTopics(false);
  };

  useEffect(() => { if (classId) reload(); /* eslint-disable-next-line */ }, [classId]);
  useEffect(() => { if (selectedChapterId) loadTopics(selectedChapterId); /* eslint-disable-next-line */ }, [selectedChapterId]);

  const handleAction = async (enrollment_id, action) => {
    setBusy(true);
    const r = await api(`/teacher/classes/${classId}/enrollment-action`, {
      method: 'POST', body: JSON.stringify({ enrollment_id, action }),
    });
    if (r.message) toast.success(r.message);
    else notifyError(r, 'Failed');
    await reload();
    onStudentsChanged && onStudentsChanged();
    setBusy(false);
  };

  const handleAddOne = async (email) => {
    setBusy(true);
    const r = await api(`/teacher/classes/${classId}/add-students`, {
      method: 'POST', body: JSON.stringify({ emails: [email] }),
    });
    if (r.added?.length) toast.success(`Added ${r.added[0].name || email}`);
    else if (r.already_enrolled?.length) toast.error('Already enrolled');
    else notifyError(r, 'Failed');
    await reload();
    onStudentsChanged && onStudentsChanged();
    setBusy(false);
  };

  const handleRemove = async (student_id) => {
    if (!(await confirmDialog({ title: 'Remove student', message: 'Remove this student from the class?', confirmLabel: 'Remove', danger: true }))) return;
    setBusy(true);
    const r = await api(`/teacher/classes/${classId}/remove-student`, {
      method: 'POST', body: JSON.stringify({ student_id }),
    });
    if (r.message) toast.success(r.message);
    else notifyError(r, 'Failed');
    await reload();
    onStudentsChanged && onStudentsChanged();
    setBusy(false);
  };

  const handleCreateChapter = async () => {
    const title = (newChapterTitle || '').trim();
    if (!title) { toast.error('Chapter title required'); return; }
    setBusy(true);
    const r = await api(`/teacher/classes/${classId}/lessons`, {
      method: 'POST',
      body: JSON.stringify({ title, description: newChapterDesc.trim() }),
    });
    if (r.lesson) {
      toast.success(`Chapter "${title}" created`);
      setNewChapterTitle(''); setNewChapterDesc(''); setShowNewChapter(false);
      setSelectedChapterId(r.lesson.id);
      await reload();
    } else {
      notifyError(r, 'Failed');
    }
    setBusy(false);
  };

  const handleDeleteChapter = async (lesson_id, title) => {
    if (!(await confirmDialog({ title: 'Delete chapter', message: `Delete chapter "${title}"? All topics inside will also be removed.`, confirmLabel: 'Delete', danger: true }))) return;
    setBusy(true);
    const r = await api(`/teacher/classes/${classId}/lessons/${lesson_id}`, { method: 'DELETE' });
    if (r.removed) {
      toast.success('Chapter removed');
      setTopicsByChapter(prev => { const c = { ...prev }; delete c[lesson_id]; return c; });
      if (selectedChapterId === lesson_id) setSelectedChapterId(null);
    } else notifyError(r, 'Failed');
    await reload();
    setBusy(false);
  };

  const beginEditChapter = (ch) => {
    setEditingChapterId(ch.id);
    setEditTitle(ch.title || '');
    setEditDesc(ch.description || '');
  };

  const handleSaveChapter = async () => {
    const title = (editTitle || '').trim();
    if (!title) { toast.error('Title cannot be empty'); return; }
    setBusy(true);
    const r = await api(`/teacher/classes/${classId}/lessons/${editingChapterId}`, {
      method: 'PUT',
      body: JSON.stringify({ title, description: editDesc.trim() }),
    });
    if (r.lesson) {
      toast.success('Chapter updated');
      setEditingChapterId(null);
      await reload();
    } else notifyError(r, 'Failed');
    setBusy(false);
  };

  const handleDeleteTopic = async (topic) => {
    if (!(await confirmDialog({ title: 'Remove topic', message: `Remove topic "${topic.title}" from this chapter?`, confirmLabel: 'Remove', danger: true }))) return;
    setBusy(true);
    const r = await api(`/teacher/topics/${topic.id}`, { method: 'DELETE' });
    if (r.removed) {
      toast.success('Topic removed');
      await loadTopics(topic.lesson_id, { force: true });
      await reload();
    } else notifyError(r, 'Failed');
    setBusy(false);
  };

  const handleCreateAssignment = async () => {
    const title = (newAssignment.title || '').trim();
    if (!newAssignmentTopicId) { toast.error('Pick a chapter and topic first'); return; }
    if (!title) { toast.error('Assignment title required'); return; }
    if (!newAssignment.dueDate) { toast.error('Due date required'); return; }
    setBusy(true);
    const r = await api('/teacher/assignments', {
      method: 'POST',
      body: JSON.stringify({
        topic_id: newAssignmentTopicId,
        title,
        description: newAssignment.description.trim(),
        due_date: newAssignment.dueDate,
        due_time: newAssignment.dueTime || '23:59',
        points: newAssignment.points,
      }),
    });
    if (r.assignment) {
      toast.success('Assignment created');
      setShowNewAssignment(false);
      setNewAssignmentChapterId(''); setNewAssignmentTopicId('');
      setNewAssignment({ title: '', description: '', dueDate: '', dueTime: '23:59', points: 0 });
      await reload();
    } else {
      notifyError(r, 'Failed');
    }
    setBusy(false);
  };

  const handleDeleteAssignment = async (assignmentId, title) => {
    if (!(await confirmDialog({ title: 'Delete assignment', message: `Delete assignment "${title}"?`, confirmLabel: 'Delete', danger: true }))) return;
    setBusy(true);
    const r = await api(`/teacher/assignments/${assignmentId}`, { method: 'DELETE' });
    if (r.deleted) { toast.success('Assignment deleted'); await reload(); } else notifyError(r, 'Failed');
    setBusy(false);
  };

  const handleViewTopic = async (topic) => {
    // Fetch full topic (with framework) to preview
    const r = await api(`/teacher/topics/${topic.id}`);
    if (r.topic) setViewingTopic(r.topic);
    else notifyError(r, 'Could not load topic');
  };

  const filteredAvailable = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return available;
    return available.filter(s => (s.full_name || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q));
  }, [available, search]);

  const selectedChapter = chapters.find(c => c.id === selectedChapterId) || null;
  const selectedTopics = selectedChapterId ? (topicsByChapter[selectedChapterId] || []) : [];
  const filteredChapters = useMemo(() => {
    const q = chapterSearch.trim().toLowerCase(); if (!q) return chapters;
    return chapters.filter(c => (c.title || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q));
  }, [chapters, chapterSearch]);
  const filteredTopics = useMemo(() => {
    const q = topicSearch.trim().toLowerCase(); if (!q) return selectedTopics;
    return selectedTopics.filter(t => (t.title || '').toLowerCase().includes(q) || (t.summary || '').toLowerCase().includes(q) || (t.key_topics || []).some(kt => (kt || '').toLowerCase().includes(q)));
  }, [selectedTopics, topicSearch]);

  if (!classId) return null;
  return (
    <div className="fixed inset-0 !m-0 z-[75] bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 md:p-6" onClick={onClose} data-testid="class-detail-drawer">
      <div
        className="w-full max-w-[1320px] h-[96vh] md:h-[92vh] bg-white rounded-xl shadow-xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="relative px-6 md:px-8 pt-6 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            {detail ? (
              <div className="flex items-center gap-4 min-w-0">
                <ClassTile size="lg" icon={Layers} color={color} />
                <div className="min-w-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <h2 className="text-2xl font-semibold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {detail.name}
                    </h2>
                    {detail.class_code && <span className="text-xs font-mono font-medium bg-slate-100 text-slate-600 px-2 h-6 inline-flex items-center rounded-md flex-shrink-0">{detail.class_code}</span>}
                  </div>
                  <AccentRule className="mt-2" color={color || undefined} />
                  <p className="mt-2 text-sm text-slate-500 flex items-center flex-wrap gap-x-2 gap-y-1">
                    {[
                      (detail.subjects || []).join(', '),
                      detail.department?.name,
                      detail.batch?.name,
                    ].filter(Boolean).map((part, i) => (
                      <React.Fragment key={part}>
                        {i > 0 && <span className="text-slate-300" aria-hidden="true">·</span>}
                        <span className="capitalize">{part}</span>
                      </React.Fragment>
                    ))}
                    <span className="text-slate-300" aria-hidden="true">·</span>
                    <span className="inline-flex items-center gap-1.5 tabular-nums"><Users className="w-4 h-4 text-slate-400" />{detail.student_count} {detail.student_count === 1 ? 'student' : 'students'}</span>
                    {detail.pending_count > 0 && (
                      <StatusDot tone="working" label={`${detail.pending_count} waiting to join`} className="text-sm text-amber-700" />
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400 py-3">Loading…</p>
            )}
            <div className="flex items-center gap-2 flex-shrink-0">
              {detail?.is_class_teacher && (
                <button onClick={() => setTab('add')}
                  className={`h-10 px-4 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${tab === 'add' ? 'bg-[#1A7F37] text-white hover:bg-[#166B2E]' : 'border border-slate-200 bg-white text-slate-900 hover:bg-slate-50'}`}
                  data-testid="drawer-tab-add">
                  <Plus className="w-4 h-4" />Add student
                </button>
              )}
              <button onClick={onClose} aria-label="Close" className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100" data-testid="drawer-close">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-5 -mx-3 overflow-x-auto">
            {[
              { id: 'lessons', label: 'Chapters', icon: BookOpen, badge: chapters.length },
              { id: 'info', label: 'Info', icon: FileText },
              { id: 'students', label: 'Students', icon: Users, badge: students.length },
              { id: 'pending', label: 'Pending', icon: CheckCircle2, badge: pending.length, highlight: pending.length > 0 },
              { id: 'assignments', label: 'Assignments', icon: ClipboardList, badge: assignments.length },
              { id: 'insights', label: 'Insights', icon: Activity },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 text-sm font-medium px-3 h-12 border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? 'text-slate-900 border-[#34A853]' : 'text-slate-500 border-transparent hover:text-slate-800'}`}
                data-testid={`drawer-tab-${t.id}`}>
                <t.icon className="w-4 h-4" />{t.label}
                {typeof t.badge === 'number' && (
                  <span className={`min-w-5 h-5 px-1.5 rounded-md text-xs tabular-nums inline-flex items-center justify-center ${t.highlight ? 'bg-amber-100 text-amber-800' : tab === t.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'}`}>{t.badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-hidden bg-slate-50/40">
          {initialLoading && <Loader />}

          {!initialLoading && tab === 'lessons' && (
            <div className="h-full flex" data-testid="drawer-lessons">
              {/* Left rail — chapters list */}
              <div className="w-[300px] border-r border-slate-100 bg-white flex flex-col overflow-hidden flex-shrink-0">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-700">Chapters</p>
                    <p className="text-xs text-slate-400">{chapters.length} total{chapterSearch && filteredChapters.length !== chapters.length ? ` · ${filteredChapters.length} shown` : ''}</p>
                  </div>
                  <button
                    onClick={() => setShowNewChapter(v => !v)}
                    className="text-xs font-medium text-white bg-[#1A7F37] hover:bg-[#166B2E] px-2.5 py-1.5 rounded-md flex items-center gap-1"
                    data-testid="chapter-new-btn"
                  >
                    <Plus className="w-3 h-3" /> New
                  </button>
                </div>

                <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/60">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={chapterSearch}
                      onChange={e => setChapterSearch(e.target.value)}
                      placeholder="Search chapters…"
                      className="w-full h-8 pl-8 pr-2 rounded-md border border-slate-200 bg-white text-xs focus:outline-none focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20"
                      data-testid="chapter-search-input"
                    />
                    {chapterSearch && (
                      <button onClick={() => setChapterSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="w-3 h-3" /></button>
                    )}
                  </div>
                </div>

                {showNewChapter && (
                  <div className="p-3 border-b border-slate-100 bg-slate-50" data-testid="create-chapter-form">
                    <input
                      value={newChapterTitle}
                      onChange={(e) => setNewChapterTitle(e.target.value)}
                      placeholder="Chapter name, e.g. Newton's Laws"
                      className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40 mb-2 bg-white"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleCreateChapter(); }}
                      data-testid="create-chapter-title"
                      autoFocus
                    />
                    <textarea
                      value={newChapterDesc}
                      onChange={(e) => setNewChapterDesc(e.target.value)}
                      placeholder="Short description (optional)"
                      rows={2}
                      className="w-full px-2.5 py-1.5 rounded-md border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40 mb-2 bg-white resize-none"
                      data-testid="create-chapter-desc"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleCreateChapter}
                        disabled={busy || !newChapterTitle.trim()}
                        className="flex-1 h-8 text-xs font-medium text-white bg-[#1A7F37] hover:bg-[#166B2E] disabled:opacity-40 rounded-md"
                        data-testid="create-chapter-submit"
                      >Create chapter</button>
                      <button
                        onClick={() => { setShowNewChapter(false); setNewChapterTitle(''); setNewChapterDesc(''); }}
                        className="h-8 text-xs font-medium text-slate-500 hover:text-slate-800 px-2"
                      >Cancel</button>
                    </div>
                  </div>
                )}

                <div className="flex-1 overflow-y-auto p-2">
                  {chapters.length === 0 && !showNewChapter && (
                    <div className="p-6 text-center">
                      <BookOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-700">No chapters yet</p>
                      <p className="text-xs text-slate-500 mt-1">Add one to group your daily topics</p>
                    </div>
                  )}
                  {chapters.length > 0 && filteredChapters.length === 0 && (
                    <div className="p-6 text-center">
                      <Search className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                      <p className="text-xs text-slate-500">No chapters match "{chapterSearch}"</p>
                    </div>
                  )}
                  {filteredChapters.map((ch) => {
                    const active = selectedChapterId === ch.id;
                    return (
                      <button
                        key={ch.id}
                        onClick={() => setSelectedChapterId(ch.id)}
                        className={`w-full text-left rounded-lg p-3 mb-1 border transition-colors ${active ? 'bg-slate-100 border-slate-200' : 'border-transparent hover:bg-slate-50'}`}
                        data-testid={`chapter-item-${ch.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${active ? 'bg-[#34A853]/10 text-[#1A7F37]' : 'bg-slate-100 text-slate-500'}`}>
                            <BookOpen className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-medium truncate ${active ? 'text-slate-900' : 'text-slate-800'}`}>{ch.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{ch.topic_count || 0} topic{(ch.topic_count || 0) !== 1 ? 's' : ''}{ch.latest_topic_date && ` · ${new Date(ch.latest_topic_date).toLocaleDateString()}`}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Main pane — chapter detail + topic timeline */}
              <div className="flex-1 overflow-y-auto">
                {!selectedChapter ? (
                  <div className="h-full flex items-center justify-center p-10">
                    <div className="text-center max-w-sm">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center mx-auto mb-3">
                        <Layers className="w-5 h-5 text-slate-500" />
                      </div>
                      <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Pick a chapter</h3>
                      <p className="text-xs text-slate-500 mt-1">Choose a chapter on the left to see the topics published in it</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 md:p-8 space-y-5">
                    {/* Chapter header card */}
                    <div className="rounded-xl border border-slate-200 bg-white p-5">
                      {editingChapterId === selectedChapter.id ? (
                        <div data-testid="edit-chapter-form">
                          <p className="text-xs font-medium text-[#1A7F37] mb-2">Edit chapter</p>
                          <input
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40 mb-2"
                            placeholder="Chapter title"
                            data-testid="edit-chapter-title"
                            autoFocus
                          />
                          <textarea
                            value={editDesc}
                            onChange={(e) => setEditDesc(e.target.value)}
                            rows={2}
                            placeholder="Short description (optional)"
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40 mb-3 resize-none"
                            data-testid="edit-chapter-desc"
                          />
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleSaveChapter}
                              disabled={busy || !editTitle.trim()}
                              className="text-xs font-medium text-white bg-[#1A7F37] hover:bg-[#166B2E] disabled:opacity-40 px-4 py-2 rounded-lg"
                              data-testid="edit-chapter-save"
                            >Save</button>
                            <button
                              onClick={() => setEditingChapterId(null)}
                              className="text-xs font-medium text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-100"
                              data-testid="edit-chapter-cancel"
                            >Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-[#1A7F37] mb-1">Chapter</p>
                            <h3 className="text-xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{selectedChapter.title}</h3>
                            {selectedChapter.description && <p className="text-xs text-slate-600 mt-1 leading-relaxed">{selectedChapter.description}</p>}
                            <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                              <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {selectedTopics.length} topic{selectedTopics.length !== 1 ? 's' : ''}</span>
                              {selectedChapter.created_at && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Created {new Date(selectedChapter.created_at).toLocaleDateString()}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => beginEditChapter(selectedChapter)}
                              disabled={busy}
                              className="text-slate-500 hover:text-[#1A7F37] p-2 rounded-lg hover:bg-[#34A853]/10 disabled:opacity-40"
                              title="Edit chapter"
                              data-testid={`chapter-edit-${selectedChapter.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteChapter(selectedChapter.id, selectedChapter.title)}
                              disabled={busy}
                              className="text-slate-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 disabled:opacity-40"
                              title="Delete chapter"
                              data-testid={`chapter-delete-${selectedChapter.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Topics timeline */}
                    <div>
                      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                        <p className="text-xs font-medium text-slate-700">Topics — date-wise</p>
                        <div className="relative flex-1 min-w-[220px] max-w-[340px]">
                          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            value={topicSearch}
                            onChange={e => setTopicSearch(e.target.value)}
                            placeholder="Search topics or lectures…"
                            className="w-full h-8 pl-8 pr-8 rounded-md border border-slate-200 bg-white text-xs focus:outline-none focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20"
                            data-testid="topic-search-input"
                          />
                          {topicSearch && (
                            <button onClick={() => setTopicSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="w-3 h-3" /></button>
                          )}
                        </div>
                        <p className="text-xs text-slate-400">Publish from Content Studio → routes here automatically</p>
                      </div>

                      {loadingTopics && <Loader label="Loading topics…" />}

                      {!loadingTopics && selectedTopics.length === 0 && (
                        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center mx-auto mb-3">
                            <Sparkles className="w-5 h-5 text-slate-500" />
                          </div>
                          <p className="text-sm font-medium text-slate-800">No topics here yet</p>
                          <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">Head over to <span className="font-medium">Content Studio</span>, edit a Deskey Session, and publish — pick this chapter and the topic will land right here, dated.</p>
                        </div>
                      )}

                      {!loadingTopics && selectedTopics.length > 0 && filteredTopics.length === 0 && (
                        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center">
                          <p className="text-xs text-slate-500">No topics match "{topicSearch}"</p>
                        </div>
                      )}

                      {!loadingTopics && filteredTopics.length > 0 && (
                        <div className="space-y-2">
                          {filteredTopics.map((topic) => (
                            <TopicRow
                              key={topic.id}
                              topic={topic}
                              onView={() => handleViewTopic(topic)}
                              onEdit={() => {
                                if (!topic.draft_id) { toast.error('No Deskey Session draft linked to this topic'); return; }
                                navigate(`/teacher?draft=${topic.draft_id}`);
                              }}
                              onDelete={() => handleDeleteTopic(topic)}
                              busy={busy}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {!initialLoading && tab === 'info' && detail && (
            <div className="p-6 md:p-8 space-y-4 overflow-y-auto h-full" data-testid="drawer-info">
              <div className="grid grid-cols-3 gap-3">
                <StatBox label="Students" value={detail.student_count} sub={`of ${detail.capacity || '∞'}`} />
                <StatBox label="Pending" value={detail.pending_count} accent="#f59e0b" />
                <StatBox label="Chapters" value={chapters.length} accent="#8B5CF6" />
              </div>
              <div className="rounded-xl bg-white border border-slate-100 p-4">
                <p className="text-xs font-medium text-slate-500 mb-2">Subjects you teach</p>
                <div className="flex gap-1.5 flex-wrap">
                  {(detail.subjects || []).map(s => (
                    <span key={s} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md">{s}</span>
                  ))}
                  {(!detail.subjects || detail.subjects.length === 0) && <span className="text-xs text-slate-400">No subjects scheduled yet</span>}
                </div>
              </div>
              {detail.schedule && detail.schedule.length > 0 && (
                <div className="rounded-xl bg-white border border-slate-100 p-4">
                  <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Your upcoming slots</p>
                  <div className="space-y-1.5">
                    {detail.schedule.slice(0, 6).map((row, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-slate-500">{row.day_of_week || ''}</span>
                        <span className="text-slate-700">{(row.slots || []).map(s => `${s.start_time}–${s.end_time} ${s.subject || ''}`).join(', ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!initialLoading && tab === 'students' && (
            <div className="p-6 md:p-8 space-y-2 overflow-y-auto h-full" data-testid="drawer-students">
              {students.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No students enrolled yet.</p>}
              {students.map(s => (
                <StudentRow key={s.id} name={s.student_name} email={s.student_email} onRemove={detail?.is_class_teacher ? () => handleRemove(s.student_id) : null} disabled={busy} testid={`student-${s.student_id}`} />
              ))}
            </div>
          )}

          {!initialLoading && tab === 'pending' && (
            <div className="p-6 md:p-8 space-y-2 overflow-y-auto h-full" data-testid="drawer-pending">
              {pending.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No pending requests.</p>}
              {pending.map(s => (
                <div key={s.id} className="flex items-center gap-3 rounded-xl border border-amber-100 bg-amber-50/40 p-3" data-testid={`pending-${s.id}`}>
                  <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 font-medium flex items-center justify-center flex-shrink-0">
                    {(s.student_name || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{s.student_name}</p>
                    <p className="text-xs text-slate-500 truncate">{s.student_email}</p>
                  </div>
                  <button onClick={() => handleAction(s.id, 'approve')} disabled={busy} className="text-xs font-medium text-white bg-[#1A7F37] hover:bg-[#166B2E] px-3 py-1.5 rounded-lg disabled:opacity-40" data-testid={`approve-${s.id}`}>Approve</button>
                  <button onClick={() => handleAction(s.id, 'reject')} disabled={busy} className="text-xs font-medium text-red-500 hover:bg-red-50 px-2 py-1.5 rounded-lg disabled:opacity-40" data-testid={`reject-${s.id}`}>Reject</button>
                </div>
              ))}
            </div>
          )}

          {!initialLoading && tab === 'assignments' && (
            <div className="p-6 md:p-8 space-y-3 overflow-y-auto h-full" data-testid="drawer-assignments">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">{assignments.length} assignment{assignments.length !== 1 ? 's' : ''} for this class</p>
                <button
                  onClick={() => setShowNewAssignment(v => !v)}
                  className="text-xs font-medium text-white bg-[#1A7F37] hover:bg-[#166B2E] px-2.5 py-1.5 rounded-md flex items-center gap-1"
                  data-testid="assignment-new-btn"
                >
                  <Plus className="w-3 h-3" /> New
                </button>
              </div>

              {showNewAssignment && (
                <div className="rounded-xl border-2 border-[#34A853]/30 bg-white overflow-hidden" data-testid="create-assignment-form">
                  <div className="p-4 pb-0 grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs font-medium text-slate-500 mb-1 block">Chapter *</span>
                      <select
                        value={newAssignmentChapterId}
                        onChange={(e) => { const id = e.target.value; setNewAssignmentChapterId(id); setNewAssignmentTopicId(''); if (id) loadTopics(id); }}
                        className="w-full h-10 px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-200"
                        data-testid="assignment-chapter-select"
                      >
                        <option value="">Select chapter…</option>
                        {chapters.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-slate-500 mb-1 block">Topic *</span>
                      <select
                        value={newAssignmentTopicId}
                        onChange={(e) => setNewAssignmentTopicId(e.target.value)}
                        disabled={!newAssignmentChapterId}
                        className="w-full h-10 px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-200 disabled:opacity-50"
                        data-testid="assignment-topic-select"
                      >
                        <option value="">{newAssignmentChapterId ? 'Select topic…' : 'Pick a chapter first'}</option>
                        {(topicsByChapter[newAssignmentChapterId] || []).map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                      </select>
                    </label>
                  </div>
                  <div className="p-4">
                    <AssignmentBlock
                      enabled
                      hideToggle
                      title={newAssignment.title}
                      description={newAssignment.description}
                      dueDate={newAssignment.dueDate}
                      dueTime={newAssignment.dueTime}
                      points={newAssignment.points}
                      onChange={(patch) => setNewAssignment(prev => ({ ...prev, ...patch }))}
                    />
                  </div>
                  <div className="px-4 pb-4 flex items-center justify-end gap-2">
                    <button onClick={() => setShowNewAssignment(false)} className="text-xs font-medium text-slate-500 hover:text-slate-800 px-3 py-2 rounded-lg">Cancel</button>
                    <button onClick={handleCreateAssignment} disabled={busy} className="text-xs font-medium text-white bg-[#1A7F37] hover:bg-[#166B2E] px-4 py-2 rounded-lg disabled:opacity-40" data-testid="assignment-create-submit">Create assignment</button>
                  </div>
                </div>
              )}

              {assignments.length === 0 && !showNewAssignment && (
                <p className="text-xs text-slate-400 text-center py-6">No assignments yet for this class.</p>
              )}
              <div className="space-y-1.5">
                {assignments.map(a => (
                  <div key={a.id} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white p-3" data-testid={`assignment-${a.id}`}>
                    <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0">
                      <ClipboardList className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{a.title}</p>
                      <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                        <CalendarDays className="w-2.5 h-2.5" /> Due {a.due_date}{a.due_time ? ` · ${a.due_time}` : ''}{a.points > 0 ? ` · ${a.points} pts` : ''}
                      </p>
                    </div>
                    <button onClick={() => handleDeleteAssignment(a.id, a.title)} disabled={busy} className="text-slate-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 disabled:opacity-40" data-testid={`assignment-delete-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!initialLoading && tab === 'insights' && (
            <div className="p-5 md:p-7 overflow-y-auto h-full" data-testid="drawer-insights">
              <ClassInsightsPanel classId={classId} />
            </div>
          )}

          {!initialLoading && tab === 'add' && detail?.is_class_teacher && (
            <div className="p-6 md:p-8 space-y-3 overflow-y-auto h-full" data-testid="drawer-add">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email…"
                  className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40"
                  data-testid="add-student-search"
                />
              </div>
              {filteredAvailable.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-6">
                  {available.length === 0 ? 'All students are already in this class.' : 'No matches.'}
                </p>
              )}
              <div className="space-y-1.5">
                {filteredAvailable.slice(0, 50).map(s => (
                  <div key={s.id} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white p-3 hover:border-[#34A853]/30 transition-colors" data-testid={`available-${s.id}`}>
                    <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-600 font-medium flex items-center justify-center flex-shrink-0">
                      {(s.full_name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{s.full_name}</p>
                      <p className="text-xs text-slate-500 truncate">{s.email}</p>
                    </div>
                    <button onClick={() => handleAddOne(s.email)} disabled={busy} className="text-xs font-medium text-white bg-[#1A7F37] hover:bg-[#166B2E] px-3 py-1.5 rounded-lg disabled:opacity-40" data-testid={`add-${s.id}`}>Add</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Topic preview — reuses the same SessionPreview the Content Editor
          uses, so a published topic renders exactly as students see it
          instead of a bespoke, drift-prone renderer. SessionPreview itself
          is `fixed inset-0` (it's meant to take over the whole screen when
          opened from the Content Editor's own full page). Nested here inside
          the My Classes popup, that would visually read as a full-page
          redirect instead of a preview INSIDE the popup — so we wrap it in a
          bounded, centered card and give that card `transform`, which makes
          it a new containing block for `position: fixed` descendants. That
          confines SessionPreview's "fixed inset-0" to the card's box instead
          of the real viewport, without needing to change SessionPreview
          itself (the Content Editor's own use of it still wants full-screen). */}
      {viewingTopic && (
        <div
          className="absolute inset-0 z-[80] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setViewingTopic(null)}
          data-testid="topic-preview"
        >
          <div
            className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl h-[85%] overflow-hidden"
            style={{ transform: 'translateZ(0)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <SessionPreview
              draft={viewingTopic}
              framework={viewingTopic.framework}
              sections={viewingTopic.framework?.sections}
              references={viewingTopic.references || []}
              onClose={() => setViewingTopic(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Topic row in timeline ────────────────────────────────────────────────────
function TopicRow({ topic, onView, onEdit, onDelete, busy }) {
  const date = topic.topic_date ? new Date(topic.topic_date) : null;
  const subtitle = (topic.summary || '').trim().slice(0, 160);
  return (
    <div className="flex items-stretch gap-3 rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-300 transition-colors group" data-testid={`topic-${topic.id}`}>
      <div className="w-14 h-14 rounded-lg bg-slate-50 border border-slate-200 flex flex-col items-center justify-center flex-shrink-0">
        <span className="text-xs text-slate-500">{date ? date.toLocaleString('en-US', { month: 'short' }) : '—'}</span>
        <span className="text-lg font-medium text-slate-900 leading-none tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>{date ? date.getDate() : '--'}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <p className="text-sm font-medium text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{topic.title}</p>
          <span className="text-xs font-medium bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Published</span>
        </div>
        {subtitle && <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">{subtitle}</p>}
        {(topic.key_topics || []).length > 0 && (
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            {(topic.key_topics || []).slice(0, 4).map((t, i) => (
              <span key={i} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md">{t}</span>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col justify-between items-end gap-1">
        <div className="flex items-center gap-1">
          <button
            onClick={onView}
            className="text-xs font-medium text-slate-700 hover:bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-md flex items-center gap-1 transition-colors"
            data-testid={`topic-view-${topic.id}`}
          >
            <Eye className="w-3 h-3" /> View
          </button>
          <button
            onClick={onEdit}
            className="text-xs font-medium text-slate-700 hover:bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-md flex items-center gap-1 transition-colors"
            title="Open in Content Editor"
            data-testid={`topic-edit-${topic.id}`}
          >
            <Pencil className="w-3 h-3" /> Edit
          </button>
        </div>
        <button
          onClick={onDelete}
          disabled={busy}
          className="text-slate-400 hover:text-red-500 p-1.5 rounded-md hover:bg-red-50 disabled:opacity-40 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Remove topic"
          data-testid={`topic-delete-${topic.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// Small helpers
function StatBox({ label, value, sub, accent }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-4">
      <p className="text-sm text-slate-600">{label}</p>
      <p className="text-xl font-medium mt-1 tabular-nums text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </div>
  );
}
function StudentRow({ name, email, onRemove, disabled, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-700 text-sm font-medium flex items-center justify-center flex-shrink-0">
        {(name || '?').slice(0, 1).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">{name}</p>
        <p className="text-sm text-slate-500 truncate">{email}</p>
      </div>
      {onRemove && <button onClick={onRemove} disabled={disabled} aria-label="Remove student" className="text-slate-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40"><Trash2 className="w-4 h-4" /></button>}
    </div>
  );
}

// ── Default export — full "My Classes" page ─────────────────────────────────
export default function TeacherMyClasses({ onQuickPick }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [openClass, setOpenClass] = useState(null); // { id, color }

  const load = async () => {
    setLoading(true);
    try {
      const res = await api('/teacher/classes/grouped');
      if (res.groups) { setGroups(res.groups); setTotal(res.total_classes || 0); }
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="px-8 py-8 max-w-7xl mx-auto space-y-8" data-testid="teacher-my-classes">
      <PageHeading title="My Classes" description={`${total} ${total === 1 ? 'class' : 'classes'} across your departments and batches`} />
      {loading ? (
        <Loader label="Loading your classes…" />
      ) : (
        <GroupedClassGrid
          groups={groups}
          onOpenClass={(c, color) => setOpenClass({ id: c.id, color })}
          onQuickPick={onQuickPick}
          emptyMessage="No classes yet. Ask your department admin to add you to a schedule."
        />
      )}
      {openClass && (
        <ClassDetailDrawer
          classId={openClass.id}
          color={openClass.color}
          onClose={() => setOpenClass(null)}
          onStudentsChanged={load}
        />
      )}
    </div>
  );
}
